import { useMemo } from 'react';
import { categoriesLoaded, useCategories } from './catalogCategories';
import { usePartCategories } from './partCategories';
import { useRunsInFlight } from './generatorMemory';
import { usePartIdentities } from './partIdentity';
import { usePartModelJobs } from './partModels';
import { groupOfCategory, isLeftRight, usePartsRun, type PartItem } from './partsRun';
import { useProductRuns } from './productRuns';
import { holesOfPart, useStepHoles } from './stepHolesRun';
import { makesOwnHole } from './stepHoles';
import { touchRows, useHardwareInteractions } from './hardwareInteractionsRun';
import { useSubmodelFilter } from './submodelFilterStore';
import { editsOf, pieceOf, type Job3D } from './threeDApi';

// THE MODELS PHASE'S SUBPHASES, and how far each has got.
//
// THE WHOLE PRODUCT FIRST, because every component part's mesh is cut out of it and out of
// nothing else (see _keep_pieces). THEN THE TOOLS, each built with the hardware it drives — the
// screwdriver with the chipboard screw and the barrel nut, the tip and the recesses cut against each
// other (see toolSet). THEN THE HARDWARE, modelled from the manual's own drawings — with the
// hardware table it is built from (a set's row, which parts are covers), which the holes are read
// off too: one subphase, the table its first half. A set there REUSES what a tool drives, built
// already, and builds the rest around it (see notice_3d._reused) — which is why the tools come
// first. THE COMPONENT PARTS, which come from the product ready-made — what is left to do on each
// is the holes the manual gives it. And the covers, placed.
//
// The tools are not counted in the phase's own progress: a movement plays without its tool (see
// clipParts), and a manual whose key nobody modelled is not unfinished. The hardware they drive is,
// under Hardware & covers, where its model is read.

export type ModelsSub = 'product' | 'tools' | 'hardware' | 'components' | 'single';

// The subphases in order, for whatever reads a remembered one back.
export const MODELS_SUBS: readonly ModelsSub[] = ['product', 'tools', 'hardware', 'components', 'single'];

// WHICH OF THE THREE KINDS A PART IS. One rule, read by the strip that files the parts under
// each subphase and by the progress below: a part flagged as a tool is a tool, and otherwise
// its category's kind decides (see groupOfCategory).
export const partKindOf = (p: PartItem | undefined, category: string | undefined,
  ontology: Parameters<typeof groupOfCategory>[1]) =>
  (p?.tool ? 'Tools' : groupOfCategory(category ?? p?.category ?? '', ontology));

// WHICH BORES ARE ALREADY IN A MESH, read off the run's own edit history: a drill adds one, a
// fill takes back whatever it filled, and a re-turn clears the lot — a lathe rebuilds the mesh
// from measurements, so nothing bored into the old one survives into it. An undo takes its
// edit off the history, so this is what is in the mesh now rather than what was ever asked for.
export type Bored = { face: string; u: number; v: number; diameter: number };

export function boredSpots(job: Job3D | null | undefined): Bored[] {
  const at: Bored[] = [];
  const spot = (k: Bored, e: { face?: string; u?: number; v?: number }) => (
    k.face === e.face && Math.abs(k.u - (e.u ?? 0)) < 0.5 && Math.abs(k.v - (e.v ?? 0)) < 0.5
  );
  editsOf(job).forEach((e) => {
    if (e.op === 'lathe') { at.length = 0; return; }
    // A BATCH, every bore it cut (see drillJobAll).
    if (e.op === 'drill_all') {
      (e.bores ?? []).forEach((b) => at.push({ face: b.face, u: b.u, v: b.v, diameter: b.diameter }));
      return;
    }
    if (!e.face || e.u == null || e.v == null) return;
    if (e.op === 'drill') {
      at.push({ face: e.face, u: e.u, v: e.v, diameter: e.diameter ?? 0 });
    } else if (e.op === 'fill' || e.op === 'fill_close' || e.op === 'fill_cap'
      || e.op === 'fill_all') {
      // WHATEVER WAS THERE, whatever width it was measured at, and however many records claim
      // it: a fill says "there is nothing here now", which is a statement about the mesh.
      for (let i = at.length - 1; i >= 0; i -= 1) if (spot(at[i], e)) at.splice(i, 1);
    }
  });
  return at;
}

export type Stage = { fraction: number; done: boolean };

const stage = (have: number, of: number): Stage => ({
  fraction: of ? Math.min(1, have / of) : 0,
  done: of > 0 && have >= of,
});

// WHILE THE PARTS DB HAS NOT ANSWERED — the first read, or a re-read after a mesh is kept — the
// last measure of this manual stands. Every stage but the interactions is counted off the parts'
// models, and counted over an unanswered list each of them is nought: the bar emptied for a
// round trip every time a switch was pressed.
const lastStages = new Map<string, Record<ModelsSub | 'all', Stage>>();
// Nothing measured yet: nought everywhere, never a full ring.
const EMPTY: Record<ModelsSub | 'all', Stage> = Object.fromEntries(
  [...MODELS_SUBS, 'all'].map((k) => [k, { fraction: 0, done: false }]),
) as Record<ModelsSub | 'all', Stage>;

export function useModelsStages(docId: string): Record<ModelsSub | 'all', Stage> {
  const { items } = usePartsRun(docId);
  const categories = usePartCategories(items);
  const ontology = useCategories();
  const identities = usePartIdentities(items);
  const { modelOf, loaded: modelsLoaded } = usePartModelJobs();
  const holes = useStepHoles(docId);
  const submodel = useSubmodelFilter(docId);
  const { runs } = useProductRuns(docId, submodel);
  const table = useHardwareInteractions(docId);

  const measured = useMemo(() => {
    const kinds = items.map((p, i) => partKindOf(p, categories[i], ontology));
    const model = (i: number) => {
      const id = identities[i]?.itemId;
      return id == null ? null : modelOf.get(id) ?? null;
    };
    // THE COVERS WITH THE REST OF THE HARDWARE, where their models are made (see coverIds).
    const hardware = items.map((_, i) => i).filter((i) => kinds[i] === 'Hardware');
    const components = items.map((_, i) => i).filter((i) => kinds[i] === 'Components');
    // A component's mesh counts only as a piece of the product: that is the one source.
    const cut = components.filter((i) => !!pieceOf(model(i)));

    // HOW MANY BORES THE MANUAL GIVES A PART, once each. A mirror pair's holes are read on
    // whichever hand the drawing shows and drilled once, into the one file both hands are.
    // NOT A POINTED SCREW'S, which is never drilled (see makesOwnHole).
    const foundOf = (i: number) => holesOfPart(holes.byStep, String(i)).map(({ hole }) => hole);
    const saidOf = (i: number) => {
      const said = foundOf(i).filter((h) => !(h.via != null && makesOwnHole(items[Number(h.via)])));
      if (!isLeftRight(items[i])) return said.length;
      const left = said.filter((h) => h.side !== 'right').length;
      return left || said.length;
    };
    // THE PARTS THE JOINTS TABLE DRILLS, and each drilled once its holes are found and bored. A part
    // whose holes are not found yet is not drilled: counted with none said, every part was "done"
    // before Find holes had answered a thing, and the ring read full.
    const toDrill = new Set(touchRows(table).flatMap((r) => r.holes)
      .filter((h) => h.kind !== 'none').map((h) => Number(h.part)));
    const drillable = components.filter((i) => toDrill.has(i));
    // FOUND, AND EVERY HOLE TO DRILL BORED — none, for a part only pointed screws go into (the seat).
    const drilled = cut.filter((i) => toDrill.has(i) && foundOf(i).length > 0 && boredSpots(model(i)).length >= saidOf(i));

    // THE KEPT PRODUCT, where the server has answered; until then the stage is the cut pieces
    // alone, rather than a product counted as not kept because nobody has asked yet.
    const product = runs === null
      ? stage(cut.length, 1 + components.length)
      : stage((runs.some((j) => j.inCatalog) ? 1 : 0) + cut.length, 1 + components.length);
    // THE HARDWARE AND ITS TABLE: each piece modelled, and the table answered — one call, so it has
    // answered or it has not. `asked` and not "has rows": an empty table after a run is an answer —
    // no two pieces of hardware meet.
    const hw = stage((table.asked ? 1 : 0) + hardware.filter((i) => !!model(i)).length, 1 + hardware.length);
    const comp = stage(drilled.length, drillable.length);
    // EACH TOOL MODELLED. Beside the three and not among them: see the note on the subphases.
    const toolAt = items.map((_, i) => i).filter((i) => kinds[i] === 'Tools');
    const tools = stage(toolAt.filter((i) => !!model(i)).length, toolAt.length);
    const all = {
      fraction: (product.fraction + hw.fraction + comp.fraction) / 3,
      done: product.done && hw.done && comp.done,
    };
    // SINGLE MODELS: every part with a model of its own, of all of them.
    const single = stage(items.filter((_, i) => !!model(i)).length, items.length);
    return { product, hardware: hw, components: comp, tools, single, all };
  }, [items, categories, ontology, identities, modelOf, holes.byStep, runs, table]);
  // AND WHILE THE ONTOLOGY HAS NOT LANDED: no part reads as hardware until it has, so the hardware
  // stage was its table alone — one of one, a full ring, then down to its real share a moment later.
  if (!modelsLoaded || !categoriesLoaded()) return lastStages.get(docId) ?? EMPTY;
  lastStages.set(docId, measured);
  return measured;
}

// WHICH SUBPHASES HAVE A PART'S MODEL BEING BUILT — for the subphase's tab, which is hatched while
// its work runs. The tab read that off the badge on screen, and the badge is the OPEN part's: a
// screwdriver being built stopped showing on the Tools tab the moment the Allen key was opened.
// A run is the run's (see generatorMemory), keyed by the part's catalogue row.
export function useGeneratingSubs(docId: string): Set<ModelsSub> {
  const { items } = usePartsRun(docId);
  const categories = usePartCategories(items);
  const ontology = useCategories();
  const identities = usePartIdentities(items);
  const flights = useRunsInFlight(docId);
  return useMemo(() => {
    const subOf = { Hardware: 'hardware', Components: 'components', Tools: 'tools' } as const;
    const out = new Set<ModelsSub>();
    // A SET BUILT TOGETHER is a run of its own, keyed by its parts' catalog ids: the tools' where
    // one of them is a tool (see toolSet), the hardware's otherwise.
    const tools = new Set(items.flatMap((p, i) => (p.tool && identities[i]?.itemId != null
      ? [String(identities[i]!.itemId)] : [])));
    for (const k of Object.keys(flights)) {
      if (!k.startsWith('set:') || !flights[k]) continue;
      out.add(k.slice(4).split('-').some((id) => tools.has(id)) ? 'tools' : 'hardware');
    }
    items.forEach((p, i) => {
      const itemId = identities[i]?.itemId;
      if (itemId == null || !flights[`part:${itemId}`]) return;
      const sub = subOf[partKindOf(p, categories[i], ontology) as keyof typeof subOf];
      if (sub) out.add(sub);
    });
    return out;
  }, [items, categories, ontology, identities, flights]);
}
